import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { CircleNotch, Trash, UploadSimple, PlusCircle, Images, MapPin } from "@phosphor-icons/react";
import api from "@/lib/api";
import { SERVICES, CITIES } from "@/seoData";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fieldCls = "w-full border border-[#E4E4E7] px-4 py-3 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#46A01E] bg-white";
const NEIGHBORHOODS = [...new Set(CITIES.flatMap((c) => [c.name, ...(c.neighborhoods || [])]))].sort();

function PhotoPicker({ label, file, onPick, testid }) {
  const ref = useRef(null);
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image.");
    if (f.size > 15 * 1024 * 1024) return toast.error("Image too large (max 15MB).");
    onPick(f);
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testid}-input`} />
      <button type="button" data-testid={`${testid}-button`} onClick={() => ref.current?.click()}
        className="w-full aspect-[4/3] flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#46A01E] bg-[#F4FAF0] hover:bg-[#EAF4E3] rounded-sm overflow-hidden transition-colors">
        {file ? <img src={URL.createObjectURL(file)} alt={label} className="w-full h-full object-cover" />
          : <><UploadSimple size={26} weight="duotone" className="text-[#46A01E]" /><span className="text-sm font-semibold text-[#15306E]">{label} photo</span></>}
      </button>
    </div>
  );
}

export default function JobStoriesTab() {
  const [items, setItems] = useState(null);
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [service, setService] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [caption, setCaption] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/job-stories").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!before || !after) return toast.error("Add both a before and an after photo.");
    if (!service || !neighborhood.trim()) return toast.error("Pick a service and a neighborhood.");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("before", before);
      fd.append("after", after);
      fd.append("service_slug", service);
      fd.append("neighborhood", neighborhood);
      fd.append("caption", caption);
      await api.post("/admin/job-stories", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Job story added — it's live on the service page.");
      setBefore(null); setAfter(null); setCaption(""); setNeighborhood("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/admin/job-stories/${id}`);
      toast.success("Job story removed.");
      load();
    } catch {
      toast.error("Delete failed.");
    } finally {
      setBusy(null);
    }
  };

  const svcName = (slug) => SERVICES.find((s) => s.slug === slug)?.name || slug;

  return (
    <div className="space-y-8">
      <form onSubmit={save} data-testid="admin-story-form" className="border border-[#E4E4E7] rounded-sm p-5 bg-white space-y-3">
        <h3 className="font-display font-extrabold text-lg text-[#15306E]">Add a before &amp; after job story</h3>
        <div className="grid grid-cols-2 gap-3">
          <PhotoPicker label="Before" file={before} onPick={setBefore} testid="admin-story-before" />
          <PhotoPicker label="After" file={after} onPick={setAfter} testid="admin-story-after" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <select data-testid="admin-story-service-select" className={fieldCls} value={service} onChange={(e) => setService(e.target.value)}>
            <option value="">Show on which service page?</option>
            {SERVICES.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
          </select>
          <input data-testid="admin-story-neighborhood-input" list="story-neighborhoods" className={fieldCls}
            placeholder="Neighborhood (e.g. North Park)" value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)} />
          <datalist id="story-neighborhoods">{NEIGHBORHOODS.map((n) => <option key={n} value={n} />)}</datalist>
        </div>
        <input data-testid="admin-story-caption-input" className={fieldCls} placeholder="Short caption (optional) — e.g. Full garage cleared in 2 hours"
          value={caption} onChange={(e) => setCaption(e.target.value)} />
        <button type="submit" disabled={saving} data-testid="admin-story-save-button"
          className="flex items-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm px-6 py-3 rounded-sm transition-colors disabled:opacity-70">
          {saving ? <CircleNotch size={18} weight="bold" className="animate-spin" /> : <><PlusCircle size={18} weight="fill" /> Add Job Story</>}
        </button>
      </form>

      {items === null ? (
        <div className="flex justify-center py-16"><CircleNotch size={32} weight="bold" className="animate-spin text-[#46A01E]" /></div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-[#D4D4D8] rounded-sm py-16 flex flex-col items-center gap-3">
          <Images size={40} weight="duotone" className="text-[#46A01E]" />
          <p className="font-display font-bold text-[#15306E]">No job stories yet</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="admin-story-list">
          {items.map((s) => (
            <div key={s.id} data-testid={`admin-story-${s.id}`} className="border border-[#E4E4E7] rounded-sm p-3 bg-white">
              <div className="grid grid-cols-2 gap-2">
                <img src={`${BACKEND}${s.before_url}`} alt="before" className="aspect-[4/3] w-full object-cover rounded-sm" />
                <img src={`${BACKEND}${s.after_url}`} alt="after" className="aspect-[4/3] w-full object-cover rounded-sm" />
              </div>
              <div className="flex items-start justify-between gap-2 mt-2">
                <div className="text-sm">
                  <p className="font-display font-bold text-[#15306E]">{svcName(s.service_slug)}</p>
                  <p className="flex items-center gap-1 text-[#52525B]"><MapPin size={13} weight="fill" className="text-[#46A01E]" /> {s.neighborhood}</p>
                  {s.caption && <p className="text-[#52525B] mt-1">{s.caption}</p>}
                </div>
                <button data-testid={`delete-story-${s.id}`} disabled={busy === s.id} onClick={() => remove(s.id)}
                  className="bg-[#F4F4F5] hover:bg-[#C0392B] hover:text-white text-[#3F3F46] rounded-full p-2 transition-colors disabled:opacity-60">
                  {busy === s.id ? <CircleNotch size={15} weight="bold" className="animate-spin" /> : <Trash size={15} weight="bold" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
